import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Typography,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Switch,
  Grid,
  Card,
  CardContent,
  CardMedia,
  Alert,
  Snackbar,
  Tabs,
  Tab,
  CircularProgress,
  LinearProgress,
  Tooltip,
} from '@mui/material';
import ReactQuill from 'react-quill-new';
import 'react-quill-new/dist/quill.snow.css';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Celebration as CelebrationIcon,
  MusicNote as MusicNoteIcon,
  LocalFlorist as SeasonalIcon,
  Star as StarIcon,
  Store as StoreIcon,
  Event as EventIcon,
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon,
  Image as ImageIcon,
  Translate as TranslateIcon,
} from '@mui/icons-material';
import { collection, query, orderBy, onSnapshot, doc, addDoc, updateDoc, deleteDoc, Timestamp, where, writeBatch } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL, deleteObject } from 'firebase/storage';
import { httpsCallable } from 'firebase/functions';
import { db, storage, functions } from '../services/firebase';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import { format } from 'date-fns';
import { ja, enUS, th } from 'date-fns/locale';

import { clamp } from '../utils/numberGuard';
// Event type definition
interface Event {
  id: string;
  title: string;
  titleEn?: string;
  titleTh?: string;
  titleZh?: string;
  titleKo?: string;
  description: string;
  descriptionEn?: string;
  descriptionTh?: string;
  descriptionZh?: string;
  descriptionKo?: string;
  imageUrl?: string;
  bannerUrl?: string;
  eventDate: Date;
  startTime: string;
  endTime: string;
  displayStartDate?: Date;
  displayEndDate?: Date;
  eventType: 'party' | 'live' | 'seasonal' | 'special' | 'popup' | 'other';
  price?: number;
  capacity?: number;
  currentBookings: number;
  reservationRequired: boolean;
  isActive: boolean;
  isSoldOut: boolean;
  displayOptions: {
    showOnCalendar: boolean;
    showBanner: boolean;
    bannerPosition: 'hero' | 'middle' | 'bottom';
  };
  createdAt: Date;
  updatedAt: Date;
}

const eventTypeConfig = {
  party: { label: 'パーティー', labelEn: 'Party', labelTh: 'ปาร์ตี้', icon: <CelebrationIcon />, color: '#e91e63' },
  live: { label: 'ライブ', labelEn: 'Live', labelTh: 'ไลฟ์', icon: <MusicNoteIcon />, color: '#9c27b0' },
  seasonal: { label: '季節イベント', labelEn: 'Seasonal', labelTh: 'เทศกาล', icon: <SeasonalIcon />, color: '#009688' },
  special: { label: '特別イベント', labelEn: 'Special', labelTh: 'พิเศษ', icon: <StarIcon />, color: '#ff9800' },
  popup: { label: 'ポップアップ', labelEn: 'Pop-up', labelTh: 'ป๊อปอัพ', icon: <StoreIcon />, color: '#3f51b5' },
  other: { label: 'その他', labelEn: 'Other', labelTh: 'อื่นๆ', icon: <EventIcon />, color: '#607d8b' },
};

/**
 * 未知の種別でも落ちないようにする。
 * 種別が増えたときや、外から入ったデータに知らない値が混ざると、
 * typeConfig が undefined になり `.color` でイベント管理の画面ごと落ちる。
 */
const fallbackEventTypeConfig = eventTypeConfig.other;
const configFor = (type: string | undefined) =>
  (type && (eventTypeConfig as Record<string, typeof fallbackEventTypeConfig>)[type]) ||
  fallbackEventTypeConfig;

const localeMap: Record<string, typeof ja> = {
  ja: ja,
  en: enUS,
  th: th,
};

// Quillエディターの設定
const quillModules = {
  toolbar: [
    [{ 'header': [1, 2, 3, false] }],
    ['bold', 'italic', 'underline', 'strike'],
    [{ 'color': [] }, { 'background': [] }],
    [{ 'list': 'ordered' }, { 'list': 'bullet' }],
    [{ 'align': [] }],
    ['link', 'image'],
    ['clean'],
  ],
};

const quillFormats = [
  'header',
  'bold', 'italic', 'underline', 'strike',
  'color', 'background',
  // Quill v2 で 'bullet' は独立したフォーマットではないため、
  // ここに書くと毎回 Cannot register "bullet" が出る。'list' だけで箇条書きは効く。
  'list',
  'align',
  'link', 'image',
];

const Events: React.FC = () => {
  const { shop, user } = useAuth();
  const { language, t } = useLanguage();
  const locale = localeMap[language] || ja;

  const [events, setEvents] = useState<Event[]>([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState<Event | null>(null);
  const [tabValue, setTabValue] = useState(0);
  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' }>({
    open: false,
    message: '',
    severity: 'success',
  });
  const [saving, setSaving] = useState(false);
  const [translating, setTranslating] = useState(false);
  const [translationProgress, setTranslationProgress] = useState<string>('');
  const [langTab, setLangTab] = useState(0); // 0=日本語, 1=English, 2=ไทย, 3=繁體中文, 4=한국어
  const [singleTranslating, setSingleTranslating] = useState(false);

  // Form state
  const [formData, setFormData] = useState({
    title: '',
    titleEn: '',
    titleTh: '',
    titleZh: '',
    titleKo: '',
    description: '',
    descriptionEn: '',
    descriptionTh: '',
    descriptionZh: '',
    descriptionKo: '',
    eventDate: format(new Date(), 'yyyy-MM-dd'),
    startTime: '18:00',
    endTime: '22:00',
    displayStartDate: '',
    displayEndDate: '',
    eventType: 'other' as Event['eventType'],
    price: '',
    capacity: '',
    reservationRequired: false,
    isActive: true,
    showOnCalendar: true,
    showBanner: false,
    bannerPosition: 'hero' as 'hero' | 'middle' | 'bottom',
  });
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [bannerFile, setBannerFile] = useState<File | null>(null);
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const [bannerPreview, setBannerPreview] = useState<string | null>(null);

  // Fetch events
  useEffect(() => {
    if (!shop?.id) {
      setLoading(false);
      return;
    }

    const eventsRef = collection(db, 'shops', shop.id, 'events');
    const q = query(eventsRef, orderBy('eventDate', 'desc'));

    const unsubscribe = onSnapshot(q, (snapshot) => {
      const eventList: Event[] = [];
      snapshot.forEach((doc) => {
        const data = doc.data();
        eventList.push({
          id: doc.id,
          title: data.title || '',
          titleEn: data.titleEn,
          titleTh: data.titleTh,
          titleZh: data.titleZh,
          titleKo: data.titleKo,
          description: data.description || '',
          descriptionEn: data.descriptionEn,
          descriptionTh: data.descriptionTh,
          descriptionZh: data.descriptionZh,
          descriptionKo: data.descriptionKo,
          imageUrl: data.imageUrl,
          bannerUrl: data.bannerUrl,
          eventDate: data.eventDate?.toDate() || new Date(),
          startTime: data.startTime || '18:00',
          endTime: data.endTime || '22:00',
          displayStartDate: data.displayStartDate?.toDate(),
          displayEndDate: data.displayEndDate?.toDate(),
          eventType: data.eventType || 'other',
          price: data.price,
          capacity: data.capacity,
          currentBookings: data.currentBookings || 0,
          reservationRequired: data.reservationRequired || false,
          isActive: data.isActive ?? true,
          isSoldOut: data.isSoldOut || false,
          displayOptions: {
            showOnCalendar: data.displayOptions?.showOnCalendar ?? true,
            showBanner: data.displayOptions?.showBanner ?? false,
            bannerPosition: data.displayOptions?.bannerPosition || 'hero',
          },
          createdAt: data.createdAt?.toDate() || new Date(),
          updatedAt: data.updatedAt?.toDate() || new Date(),
        });
      });
      setEvents(eventList);
      setLoading(false);
    });

    return () => unsubscribe();
  }, [shop?.id]);

  const handleOpenDialog = (event?: Event) => {
    if (event) {
      setSelectedEvent(event);
      setFormData({
        title: event.title,
        titleEn: event.titleEn || '',
        titleTh: event.titleTh || '',
        titleZh: event.titleZh || '',
        titleKo: event.titleKo || '',
        description: event.description,
        descriptionEn: event.descriptionEn || '',
        descriptionTh: event.descriptionTh || '',
        descriptionZh: event.descriptionZh || '',
        descriptionKo: event.descriptionKo || '',
        eventDate: format(event.eventDate, 'yyyy-MM-dd'),
        startTime: event.startTime,
        endTime: event.endTime,
        displayStartDate: event.displayStartDate ? format(event.displayStartDate, 'yyyy-MM-dd') : '',
        displayEndDate: event.displayEndDate ? format(event.displayEndDate, 'yyyy-MM-dd') : '',
        eventType: event.eventType,
        price: event.price?.toString() || '',
        capacity: event.capacity?.toString() || '',
        reservationRequired: event.reservationRequired,
        isActive: event.isActive,
        showOnCalendar: event.displayOptions.showOnCalendar,
        showBanner: event.displayOptions.showBanner,
        bannerPosition: event.displayOptions.bannerPosition,
      });
      setImagePreview(event.imageUrl || null);
      setBannerPreview(event.bannerUrl || null);
    } else {
      setSelectedEvent(null);
      setFormData({
        title: '',
        titleEn: '',
        titleTh: '',
        titleZh: '',
        titleKo: '',
        description: '',
        descriptionEn: '',
        descriptionTh: '',
        descriptionZh: '',
        descriptionKo: '',
        eventDate: format(new Date(), 'yyyy-MM-dd'),
        startTime: '18:00',
        endTime: '22:00',
        displayStartDate: '',
        displayEndDate: '',
        eventType: 'other',
        price: '',
        capacity: '',
        reservationRequired: false,
        isActive: true,
        showOnCalendar: true,
        showBanner: false,
        bannerPosition: 'hero',
      });
      setImagePreview(null);
      setBannerPreview(null);
    }
    setImageFile(null);
    setBannerFile(null);
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setSelectedEvent(null);
  };

  const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>, type: 'image' | 'banner') => {
    const file = e.target.files?.[0];
    if (file) {
      if (type === 'image') {
        setImageFile(file);
        setImagePreview(URL.createObjectURL(file));
      } else {
        setBannerFile(file);
        setBannerPreview(URL.createObjectURL(file));
      }
    }
  };

  const uploadImage = async (file: File, path: string): Promise<string> => {
    const storageRef = ref(storage, path);
    await uploadBytes(storageRef, file);
    return await getDownloadURL(storageRef);
  };

  const handleSave = async () => {
    if (!shop?.id || !formData.title) return;

    setSaving(true);
    try {
      let imageUrl = selectedEvent?.imageUrl;
      let bannerUrl = selectedEvent?.bannerUrl;

      // Upload new image if selected
      if (imageFile) {
        const timestamp = Date.now();
        const path = `shops/${shop.id}/events/main_${timestamp}_${imageFile.name}`;
        imageUrl = await uploadImage(imageFile, path);
      }

      // Upload new banner if selected
      if (bannerFile) {
        const timestamp = Date.now();
        const path = `shops/${shop.id}/events/banner_${timestamp}_${bannerFile.name}`;
        bannerUrl = await uploadImage(bannerFile, path);
      }

      const eventData = {
        shopId: shop.id,
        title: formData.title,
        titleEn: formData.titleEn || null,
        titleTh: formData.titleTh || null,
        titleZh: formData.titleZh || null,
        titleKo: formData.titleKo || null,
        description: formData.description,
        descriptionEn: formData.descriptionEn || null,
        descriptionTh: formData.descriptionTh || null,
        descriptionZh: formData.descriptionZh || null,
        descriptionKo: formData.descriptionKo || null,
        imageUrl: imageUrl || null,
        bannerUrl: bannerUrl || null,
        eventDate: Timestamp.fromDate(new Date(formData.eventDate)),
        startTime: formData.startTime,
        endTime: formData.endTime,
        displayStartDate: formData.displayStartDate ? Timestamp.fromDate(new Date(formData.displayStartDate)) : null,
        displayEndDate: formData.displayEndDate ? Timestamp.fromDate(new Date(formData.displayEndDate)) : null,
        eventType: formData.eventType,
        // 負の参加費・負の定員は意味を持たない。0以上に丸める。
        price: formData.price ? clamp(formData.price, 0, Number.MAX_SAFE_INTEGER) : null,
        capacity: formData.capacity ? clamp(formData.capacity, 0, 100000) : null,
        reservationRequired: formData.reservationRequired,
        isActive: formData.isActive,
        displayOptions: {
          showOnCalendar: formData.showOnCalendar,
          showBanner: formData.showBanner,
          bannerPosition: formData.bannerPosition,
        },
        updatedAt: Timestamp.now(),
      };

      if (selectedEvent) {
        // Update existing event
        await updateDoc(doc(db, 'shops', shop.id, 'events', selectedEvent.id), eventData);
        setSnackbar({ open: true, message: t('success'), severity: 'success' });
      } else {
        // Create new event
        await addDoc(collection(db, 'shops', shop.id, 'events'), {
          ...eventData,
          currentBookings: 0,
          isSoldOut: false,
          createdAt: Timestamp.now(),
          createdBy: user?.uid,
        });
        setSnackbar({ open: true, message: t('success'), severity: 'success' });
      }

      handleCloseDialog();
    } catch (error) {
      console.error('Error saving event:', error);
      setSnackbar({ open: true, message: t('error'), severity: 'error' });
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!shop?.id || !selectedEvent) return;

    try {
      await deleteDoc(doc(db, 'shops', shop.id, 'events', selectedEvent.id));
      setSnackbar({ open: true, message: t('success'), severity: 'success' });
      setDeleteDialogOpen(false);
      setSelectedEvent(null);
    } catch (error) {
      console.error('Error deleting event:', error);
      setSnackbar({ open: true, message: t('error'), severity: 'error' });
    }
  };

  const handleToggleActive = async (event: Event) => {
    if (!shop?.id) return;

    try {
      await updateDoc(doc(db, 'shops', shop.id, 'events', event.id), {
        isActive: !event.isActive,
        updatedAt: Timestamp.now(),
      });
    } catch (error) {
      console.error('Error toggling event:', error);
      setSnackbar({ open: true, message: t('error'), severity: 'error' });
    }
  };

  // 単一イベントのAI翻訳
  const handleSingleTranslate = async () => {
    if (!shop?.id || !formData.title) {
      setSnackbar({ open: true, message: '日本語のタイトルを入力してください', severity: 'error' });
      return;
    }

    setSingleTranslating(true);

    try {
      const translateFn = httpsCallable(functions, 'translateMenuItems', { timeout: 60000 });

      const result = await translateFn({
        shopName: shop.shopName,
        businessTypeCode: shop.businessTypeCode || 'restaurant',
        sourceLanguage: 'ja',
        targetLanguages: ['en', 'th', 'zh-TW', 'ko'],
        items: [{
          type: 'event',
          id: 'temp',
          name: formData.title,
          description: formData.description || '',
        }],
        batchSize: 1,
        forceRetranslate: true,
      });

      const data = result.data as { translations: any[] };

      if (data.translations && data.translations.length > 0) {
        const translation = data.translations[0];
        setFormData(prev => ({
          ...prev,
          titleEn: translation.nameEn || prev.titleEn,
          titleTh: translation.nameTh || prev.titleTh,
          titleZh: translation.nameZhTw || prev.titleZh,
          titleKo: translation.nameKo || prev.titleKo,
          descriptionEn: translation.descriptionEn || prev.descriptionEn,
          descriptionTh: translation.descriptionTh || prev.descriptionTh,
          descriptionZh: translation.descriptionZhTw || prev.descriptionZh,
          descriptionKo: translation.descriptionKo || prev.descriptionKo,
        }));
        setSnackbar({ open: true, message: '翻訳完了しました', severity: 'success' });
      }
    } catch (error: any) {
      console.error('翻訳エラー:', error);
      setSnackbar({ open: true, message: `翻訳に失敗しました: ${error.message || ''}`, severity: 'error' });
    } finally {
      setSingleTranslating(false);
    }
  };

  // AI一括翻訳
  const handleBatchTranslate = async () => {
    if (!shop?.id || events.length === 0) return;

    const confirmMessage = `全${events.length}件のイベントを一括翻訳しますか？\n` +
      `※日本語のタイトル・説明文を英語・タイ語・中国語・韓国語に翻訳します\n` +
      `※処理には数分かかる場合があります`;

    const confirmed = window.confirm(confirmMessage);
    if (!confirmed) return;

    setTranslating(true);
    setTranslationProgress('翻訳準備中...');

    try {
      const sourceLanguage = 'ja';
      const targetLanguages: ('en' | 'th' | 'zh-TW' | 'ko')[] = ['en', 'th', 'zh-TW', 'ko'];

      // イベントデータを翻訳用に整形
      const items = events.map(event => ({
        type: 'event',
        id: event.id,
        name: event.title,
        description: event.description || '',
      }));

      const translateFn = httpsCallable(functions, 'translateMenuItems', { timeout: 600000 });

      setTranslationProgress(`翻訳中... (${items.length}件)`);

      const result = await translateFn({
        shopName: shop.shopName,
        businessTypeCode: shop.businessTypeCode || 'restaurant',
        sourceLanguage,
        targetLanguages,
        items,
        batchSize: 10,
        forceRetranslate: true,
      });

      const data = result.data as { translations: any[]; skipped: number };

      setTranslationProgress('データベースを更新中...');

      // Batch Writeで一括更新
      const batch = writeBatch(db);
      let updateCount = 0;

      for (const translation of data.translations) {
        const eventRef = doc(db, 'shops', shop.id, 'events', translation.id);
        const updateData: any = {
          updatedAt: Timestamp.now(),
        };

        // 翻訳先の言語フィールドのみ更新
        if (translation.nameEn) updateData.titleEn = translation.nameEn;
        if (translation.nameTh) updateData.titleTh = translation.nameTh;
        if (translation.nameZhTw) updateData.titleZh = translation.nameZhTw;
        if (translation.nameKo) updateData.titleKo = translation.nameKo;

        if (translation.descriptionEn) updateData.descriptionEn = translation.descriptionEn;
        if (translation.descriptionTh) updateData.descriptionTh = translation.descriptionTh;
        if (translation.descriptionZhTw) updateData.descriptionZh = translation.descriptionZhTw;
        if (translation.descriptionKo) updateData.descriptionKo = translation.descriptionKo;

        batch.update(eventRef, updateData);
        updateCount++;
      }

      await batch.commit();

      setSnackbar({
        open: true,
        message: `翻訳完了: ${updateCount}件を更新しました`,
        severity: 'success'
      });
    } catch (error: any) {
      console.error('一括翻訳エラー:', error);
      setSnackbar({
        open: true,
        message: `翻訳に失敗しました: ${error.message || '不明なエラー'}`,
        severity: 'error'
      });
    } finally {
      setTranslating(false);
      setTranslationProgress('');
    }
  };

  const getEventTypeLabel = (type: Event['eventType']) => {
    const config = configFor(type);
    if (language === 'en') return config.labelEn;
    if (language === 'th') return config.labelTh;
    return config.label;
  };

  // Filter events based on tab
  const now = new Date();
  const upcomingEvents = events.filter(e => e.eventDate >= new Date(now.setHours(0, 0, 0, 0)));
  const pastEvents = events.filter(e => e.eventDate < new Date(now.setHours(0, 0, 0, 0)));
  const displayedEvents = tabValue === 0 ? upcomingEvents : pastEvents;

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" fontWeight="bold">
          <CelebrationIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
          {t('eventManagement')}
        </Typography>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button
            variant="outlined"
            startIcon={<TranslateIcon />}
            onClick={handleBatchTranslate}
            disabled={translating || events.length === 0}
          >
            {translating ? translationProgress : '一括翻訳 (AI)'}
          </Button>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => handleOpenDialog()}
          >
            {t('addEvent')}
          </Button>
        </Box>
      </Box>

      <Tabs value={tabValue} onChange={(_, v) => setTabValue(v)} sx={{ mb: 2 }}>
        <Tab label={`${t('upcoming')} (${upcomingEvents.length})`} />
        <Tab label={`${t('past')} (${pastEvents.length})`} />
      </Tabs>

      {displayedEvents.length === 0 ? (
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <EventIcon sx={{ fontSize: 60, color: 'text.disabled', mb: 2 }} />
          <Typography color="text.secondary">{t('noData')}</Typography>
        </Paper>
      ) : (
        <Grid container spacing={2}>
          {displayedEvents.map((event) => {
            const typeConfig = configFor(event.eventType);
            const remainingCapacity = event.capacity ? event.capacity - event.currentBookings : null;
            const isPast = event.eventDate < new Date();

            return (
              <Grid item xs={12} sm={6} md={4} key={event.id}>
                <Card sx={{ height: '100%', opacity: isPast ? 0.7 : 1, position: 'relative' }}>
                  {event.imageUrl ? (
                    <CardMedia
                      component="img"
                      height="160"
                      image={event.imageUrl}
                      alt={event.title}
                    />
                  ) : (
                    <Box
                      sx={{
                        height: 160,
                        bgcolor: typeConfig.color,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: 'white',
                      }}
                    >
                      {React.cloneElement(typeConfig.icon, { sx: { fontSize: 60 } })}
                    </Box>
                  )}

                  {/* Status badges */}
                  <Box sx={{ position: 'absolute', top: 8, right: 8, display: 'flex', gap: 0.5 }}>
                    {!event.isActive && (
                      <Chip label={t('hidden')} size="small" color="default" />
                    )}
                    {event.isSoldOut && (
                      <Chip label={t('soldOut')} size="small" color="error" />
                    )}
                  </Box>

                  <CardContent>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                      <Chip
                        icon={typeConfig.icon}
                        label={getEventTypeLabel(event.eventType)}
                        size="small"
                        sx={{ bgcolor: `${typeConfig.color}20`, color: typeConfig.color }}
                      />
                    </Box>

                    <Typography variant="h6" fontWeight="bold" noWrap>
                      {event.title}
                    </Typography>

                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                      {format(event.eventDate, 'yyyy/MM/dd (E)', { locale })} {event.startTime} - {event.endTime}
                    </Typography>

                    {event.capacity && (
                      <Box sx={{ mb: 1 }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                          <Typography variant="caption">
                            {t('capacity')}: {event.currentBookings}/{event.capacity}
                          </Typography>
                          {remainingCapacity !== null && remainingCapacity > 0 && (
                            <Typography variant="caption" color="success.main">
                              {t('remaining')}{remainingCapacity}{t('seats')}
                            </Typography>
                          )}
                        </Box>
                        <LinearProgress
                          variant="determinate"
                          value={(event.currentBookings / event.capacity) * 100}
                          color={remainingCapacity === 0 ? 'error' : 'primary'}
                        />
                      </Box>
                    )}

                    {event.price !== undefined && event.price !== null && (
                      <Typography variant="body2" fontWeight="bold" color="primary">
                        ¥{event.price.toLocaleString()}
                      </Typography>
                    )}

                    <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, mt: 2 }}>
                      <Tooltip title={event.isActive ? t('hide') : t('show')}>
                        <IconButton
                          size="small"
                          onClick={() => handleToggleActive(event)}
                        >
                          {event.isActive ? <VisibilityIcon /> : <VisibilityOffIcon />}
                        </IconButton>
                      </Tooltip>
                      <IconButton
                        size="small"
                        onClick={() => handleOpenDialog(event)}
                      >
                        <EditIcon />
                      </IconButton>
                      <IconButton
                        size="small"
                        color="error"
                        onClick={() => {
                          setSelectedEvent(event);
                          setDeleteDialogOpen(true);
                        }}
                      >
                        <DeleteIcon />
                      </IconButton>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            );
          })}
        </Grid>
      )}

      {/* Edit/Create Dialog */}
      <Dialog open={dialogOpen} onClose={handleCloseDialog} maxWidth="md" fullWidth>
        <DialogTitle>
          {selectedEvent ? t('editEvent') : t('addEvent')}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            {/* Event Type */}
            <FormControl fullWidth>
              <InputLabel>{t('eventType')}</InputLabel>
              <Select
                value={formData.eventType}
                label={t('eventType')}
                onChange={(e) => setFormData({ ...formData, eventType: e.target.value as Event['eventType'] })}
              >
                {Object.entries(eventTypeConfig).map(([key, config]) => (
                  <MenuItem key={key} value={key}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      {config.icon}
                      {language === 'en' ? config.labelEn : language === 'th' ? config.labelTh : config.label}
                    </Box>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {/* Title & Description with Language Tabs */}
            <Paper variant="outlined" sx={{ p: 2 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>
                  {t('title')} / {t('description')}
                </Typography>
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={singleTranslating ? <CircularProgress size={16} /> : <TranslateIcon />}
                  onClick={handleSingleTranslate}
                  disabled={singleTranslating || !formData.title}
                >
                  {singleTranslating ? '翻訳中...' : 'AI翻訳'}
                </Button>
              </Box>
              <Tabs
                value={langTab}
                onChange={(_, v) => setLangTab(v)}
                variant="scrollable"
                scrollButtons="auto"
                sx={{ mb: 2, borderBottom: 1, borderColor: 'divider' }}
              >
                <Tab label="日本語" />
                <Tab label="English" />
                <Tab label="ไทย" />
                <Tab label="繁體中文" />
                <Tab label="한국어" />
              </Tabs>

              {/* 日本語 */}
              {langTab === 0 && (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  <TextField
                    label={`${t('title')} (日本語) *`}
                    value={formData.title}
                    onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                    required
                    fullWidth
                  />
                  <Box>
                    <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5, display: 'block' }}>
                      {t('description')} (日本語)
                    </Typography>
                    <Box sx={{
                      '& .ql-container': { minHeight: '150px', fontSize: '14px', borderBottomLeftRadius: '4px', borderBottomRightRadius: '4px' },
                      '& .ql-editor': { minHeight: '150px' },
                      '& .ql-toolbar': { borderTopLeftRadius: '4px', borderTopRightRadius: '4px' },
                    }}>
                      <ReactQuill
                        theme="snow"
                        value={formData.description}
                        onChange={(value) => setFormData({ ...formData, description: value })}
                        modules={quillModules}
                        formats={quillFormats}
                        placeholder="イベントの詳細を入力してください"
                      />
                    </Box>
                  </Box>
                </Box>
              )}

              {/* English */}
              {langTab === 1 && (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  <TextField
                    label={`${t('title')} (English)`}
                    value={formData.titleEn}
                    onChange={(e) => setFormData({ ...formData, titleEn: e.target.value })}
                    fullWidth
                    helperText="AI翻訳で自動入力できます"
                  />
                  <Box>
                    <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5, display: 'block' }}>
                      {t('description')} (English)
                    </Typography>
                    <Box sx={{
                      '& .ql-container': { minHeight: '150px', fontSize: '14px', borderBottomLeftRadius: '4px', borderBottomRightRadius: '4px' },
                      '& .ql-editor': { minHeight: '150px' },
                      '& .ql-toolbar': { borderTopLeftRadius: '4px', borderTopRightRadius: '4px' },
                    }}>
                      <ReactQuill
                        theme="snow"
                        value={formData.descriptionEn}
                        onChange={(value) => setFormData({ ...formData, descriptionEn: value })}
                        modules={quillModules}
                        formats={quillFormats}
                        placeholder="Enter event details"
                      />
                    </Box>
                  </Box>
                </Box>
              )}

              {/* ไทย */}
              {langTab === 2 && (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  <TextField
                    label={`${t('title')} (ไทย)`}
                    value={formData.titleTh}
                    onChange={(e) => setFormData({ ...formData, titleTh: e.target.value })}
                    fullWidth
                    helperText="AI翻訳で自動入力できます"
                  />
                  <Box>
                    <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5, display: 'block' }}>
                      {t('description')} (ไทย)
                    </Typography>
                    <Box sx={{
                      '& .ql-container': { minHeight: '150px', fontSize: '14px', borderBottomLeftRadius: '4px', borderBottomRightRadius: '4px' },
                      '& .ql-editor': { minHeight: '150px' },
                      '& .ql-toolbar': { borderTopLeftRadius: '4px', borderTopRightRadius: '4px' },
                    }}>
                      <ReactQuill
                        theme="snow"
                        value={formData.descriptionTh}
                        onChange={(value) => setFormData({ ...formData, descriptionTh: value })}
                        modules={quillModules}
                        formats={quillFormats}
                        placeholder="ป้อนรายละเอียดกิจกรรม"
                      />
                    </Box>
                  </Box>
                </Box>
              )}

              {/* 繁體中文 */}
              {langTab === 3 && (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  <TextField
                    label={`${t('title')} (繁體中文)`}
                    value={formData.titleZh}
                    onChange={(e) => setFormData({ ...formData, titleZh: e.target.value })}
                    fullWidth
                    helperText="AI翻訳で自動入力できます"
                  />
                  <Box>
                    <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5, display: 'block' }}>
                      {t('description')} (繁體中文)
                    </Typography>
                    <Box sx={{
                      '& .ql-container': { minHeight: '150px', fontSize: '14px', borderBottomLeftRadius: '4px', borderBottomRightRadius: '4px' },
                      '& .ql-editor': { minHeight: '150px' },
                      '& .ql-toolbar': { borderTopLeftRadius: '4px', borderTopRightRadius: '4px' },
                    }}>
                      <ReactQuill
                        theme="snow"
                        value={formData.descriptionZh}
                        onChange={(value) => setFormData({ ...formData, descriptionZh: value })}
                        modules={quillModules}
                        formats={quillFormats}
                        placeholder="輸入活動詳情"
                      />
                    </Box>
                  </Box>
                </Box>
              )}

              {/* 한국어 */}
              {langTab === 4 && (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  <TextField
                    label={`${t('title')} (한국어)`}
                    value={formData.titleKo}
                    onChange={(e) => setFormData({ ...formData, titleKo: e.target.value })}
                    fullWidth
                    helperText="AI翻訳で自動入力できます"
                  />
                  <Box>
                    <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5, display: 'block' }}>
                      {t('description')} (한국어)
                    </Typography>
                    <Box sx={{
                      '& .ql-container': { minHeight: '150px', fontSize: '14px', borderBottomLeftRadius: '4px', borderBottomRightRadius: '4px' },
                      '& .ql-editor': { minHeight: '150px' },
                      '& .ql-toolbar': { borderTopLeftRadius: '4px', borderTopRightRadius: '4px' },
                    }}>
                      <ReactQuill
                        theme="snow"
                        value={formData.descriptionKo}
                        onChange={(value) => setFormData({ ...formData, descriptionKo: value })}
                        modules={quillModules}
                        formats={quillFormats}
                        placeholder="이벤트 상세 정보를 입력하세요"
                      />
                    </Box>
                  </Box>
                </Box>
              )}
            </Paper>

            {/* Date & Time */}
            <Grid container spacing={2}>
              <Grid item xs={12} sm={4}>
                <TextField
                  label={t('date')}
                  type="date"
                  value={formData.eventDate}
                  onChange={(e) => setFormData({ ...formData, eventDate: e.target.value })}
                  fullWidth
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              <Grid item xs={6} sm={4}>
                <TextField
                  label={t('startTime')}
                  type="time"
                  value={formData.startTime}
                  onChange={(e) => setFormData({ ...formData, startTime: e.target.value })}
                  fullWidth
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              <Grid item xs={6} sm={4}>
                <TextField
                  label={t('endTime')}
                  type="time"
                  value={formData.endTime}
                  onChange={(e) => setFormData({ ...formData, endTime: e.target.value })}
                  fullWidth
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
            </Grid>

            {/* Price & Capacity */}
            <Grid container spacing={2}>
              <Grid item xs={6}>
                <TextField
                  label={t('price')}
                  type="number"
                  value={formData.price}
                  onChange={(e) => setFormData({ ...formData, price: e.target.value })}
                  fullWidth
                  InputProps={{ startAdornment: '¥' }}
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  label={t('capacity')}
                  type="number"
                  value={formData.capacity}
                  onChange={(e) => setFormData({ ...formData, capacity: e.target.value })}
                  fullWidth
                />
              </Grid>
            </Grid>

            {/* Display Period */}
            <Typography variant="subtitle2" color="text.secondary" sx={{ mt: 1 }}>
              {t('displayPeriod')}
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={6}>
                <TextField
                  label={t('startDate')}
                  type="date"
                  value={formData.displayStartDate}
                  onChange={(e) => setFormData({ ...formData, displayStartDate: e.target.value })}
                  fullWidth
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  label={t('endDate')}
                  type="date"
                  value={formData.displayEndDate}
                  onChange={(e) => setFormData({ ...formData, displayEndDate: e.target.value })}
                  fullWidth
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
            </Grid>

            {/* Images */}
            <Typography variant="subtitle2" color="text.secondary" sx={{ mt: 1 }}>
              {t('images')}
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={6}>
                <Button
                  variant="outlined"
                  component="label"
                  fullWidth
                  startIcon={<ImageIcon />}
                >
                  {t('mainImage')}
                  <input
                    type="file"
                    hidden
                    accept="image/*"
                    onChange={(e) => handleImageChange(e, 'image')}
                  />
                </Button>
                {imagePreview && (
                  <Box sx={{ mt: 1 }}>
                    <img src={imagePreview} alt="Preview" style={{ width: '100%', maxHeight: 120, objectFit: 'cover', borderRadius: 4 }} />
                  </Box>
                )}
              </Grid>
              <Grid item xs={6}>
                <Button
                  variant="outlined"
                  component="label"
                  fullWidth
                  startIcon={<ImageIcon />}
                >
                  {t('bannerImage')}
                  <input
                    type="file"
                    hidden
                    accept="image/*"
                    onChange={(e) => handleImageChange(e, 'banner')}
                  />
                </Button>
                {bannerPreview && (
                  <Box sx={{ mt: 1 }}>
                    <img src={bannerPreview} alt="Banner Preview" style={{ width: '100%', maxHeight: 120, objectFit: 'cover', borderRadius: 4 }} />
                  </Box>
                )}
              </Grid>
            </Grid>

            {/* Options */}
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mt: 1 }}>
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.isActive}
                    onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
                  />
                }
                label={t('active')}
              />
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.reservationRequired}
                    onChange={(e) => setFormData({ ...formData, reservationRequired: e.target.checked })}
                  />
                }
                label={t('reservationRequired')}
              />
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.showOnCalendar}
                    onChange={(e) => setFormData({ ...formData, showOnCalendar: e.target.checked })}
                  />
                }
                label={t('showOnCalendar')}
              />
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.showBanner}
                    onChange={(e) => setFormData({ ...formData, showBanner: e.target.checked })}
                  />
                }
                label={t('showBanner')}
              />
            </Box>

            {formData.showBanner && (
              <FormControl fullWidth>
                <InputLabel>{t('bannerPosition')}</InputLabel>
                <Select
                  value={formData.bannerPosition}
                  label={t('bannerPosition')}
                  onChange={(e) => setFormData({ ...formData, bannerPosition: e.target.value as 'hero' | 'middle' | 'bottom' })}
                >
                  <MenuItem value="hero">{t('heroPosition')}</MenuItem>
                  <MenuItem value="middle">{t('middlePosition')}</MenuItem>
                  <MenuItem value="bottom">{t('bottomPosition')}</MenuItem>
                </Select>
              </FormControl>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>{t('cancel')}</Button>
          <Button
            variant="contained"
            onClick={handleSave}
            disabled={saving || !formData.title}
          >
            {saving ? <CircularProgress size={24} /> : t('save')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>{t('confirm')}</DialogTitle>
        <DialogContent>
          <Typography>
            {t('deleteConfirm')}
          </Typography>
          {selectedEvent && (
            <Typography fontWeight="bold" sx={{ mt: 1 }}>
              {selectedEvent.title}
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>{t('cancel')}</Button>
          <Button variant="contained" color="error" onClick={handleDelete}>
            {t('delete')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert severity={snackbar.severity} onClose={() => setSnackbar({ ...snackbar, open: false })}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default Events;
